import React, { useEffect, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { useAnalyticsTasks } from '../../hooks/useApi';
import { downloadCSV } from '../../utils/csvExport';
import {
    Button,
    Card,
    CardHeader,
    DataTable,
    EmptyState,
    Pagination,
    PriorityBadge,
    StatusBadge,
    useSort,
} from '../../components/ui';

const isOverdue = (task) =>
    task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date();

const TasksTab = ({ filters }) => {
    const [page, setPage] = useState(1);
    const { data: result, isLoading } = useAnalyticsTasks({ ...filters, page });
    const tasks = useMemo(() => result?.data || [], [result]);
    const { sort, onSort } = useSort({ key: 'due_date', dir: 'asc' });

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const sortedTasks = useMemo(() => {
        if (!sort) return tasks;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...tasks].sort((a, b) => {
            let av = a[sort.key];
            let bv = b[sort.key];
            if (sort.key === 'due_date') {
                return ((new Date(av || 0).getTime() || 0) - (new Date(bv || 0).getTime() || 0)) * dir;
            }
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [tasks, sort]);

    const totalPages = result?.totalPages || 1;
    const total = result?.total || sortedTasks.length;

    const columns = [
        {
            key: 'title',
            header: 'Task',
            sortable: true,
            render: (t) => (
                <>
                    <span className="block font-medium text-ink-900 truncate max-w-[260px]">{t.title}</span>
                    <span className="block text-micro font-mono text-ink-500 mt-0.5">{t.task_code}</span>
                </>
            ),
        },
        {
            key: 'project_name',
            header: 'Project',
            sortable: true,
            render: (t) => (
                <span className="block text-ink-700 truncate max-w-[160px]" title={t.project_name}>
                    {t.project_name || '—'}
                </span>
            ),
        },
        {
            key: 'assignee_names',
            header: 'Assignees',
            sortable: true,
            render: (t) =>
                t.assignee_names ? (
                    <span className="block text-ink-700 truncate max-w-[160px]" title={t.assignee_names}>
                        {t.assignee_names}
                    </span>
                ) : (
                    <span className="text-muted">Unassigned</span>
                ),
        },
        {
            key: 'priority',
            header: 'Priority',
            sortable: true,
            render: (t) => <PriorityBadge priority={t.priority} />,
        },
        {
            key: 'status',
            header: 'Status',
            sortable: true,
            render: (t) => <StatusBadge status={t.status} />,
        },
        {
            key: 'due_date',
            header: 'Due date',
            sortable: true,
            align: 'right',
            render: (t) => (
                <span
                    className={`tabular whitespace-nowrap ${isOverdue(t) ? 'text-danger font-medium' : 'text-ink-600'}`}
                >
                    {t.due_date
                        ? new Date(t.due_date).toLocaleDateString(undefined, {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                        })
                        : '—'}
                </span>
            ),
        },
    ];

    return (
        <Card>
            <CardHeader
                title="Task analysis"
                subtitle="Every task matching the current filters"
                actions={
                    <Button
                        size="sm"
                        icon={Download}
                        className="no-print"
                        onClick={() => downloadCSV(sortedTasks, 'tasks-report.csv')}
                        disabled={!sortedTasks.length}
                    >
                        Export CSV
                    </Button>
                }
            />

            <DataTable
                columns={columns}
                rows={sortedTasks}
                loading={isLoading}
                sort={sort}
                onSort={onSort}
                empty={
                    <EmptyState
                        title="No tasks match this selection"
                        description="Adjust the filters or widen the date range."
                    />
                }
            />

            {sortedTasks.length > 0 && totalPages > 1 && (
                <Pagination
                    className="no-print"
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={Math.ceil(total / totalPages)}
                    onPageChange={setPage}
                />
            )}
        </Card>
    );
};

export default TasksTab;
